import React, { createContext, useState, useContext, useEffect } from 'react';
import apiService from '../services/api';

// Helper to ensure profileImageUrl is an absolute URL pointing to the API server if it's a relative path
const formatProfileImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Strip '/api' from the end of the base URL
  const apiHost = apiService.API_BASE_URL.replace(/\/api$/, '');
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${apiHost}${cleanPath}`;
};

// Create Auth Context
const AuthContext = createContext(null);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount and set up unauthorized listener
  useEffect(() => {
    const storedUser = localStorage.getItem('vedic_ai_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser) {
          parsedUser.profileImageUrl = formatProfileImageUrl(parsedUser.profileImageUrl);
          localStorage.setItem('vedic_ai_user', JSON.stringify(parsedUser));
        }
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('vedic_ai_user');
      }
    }
    setLoading(false);

    const handleUnauthorized = () => {
      setUser(null);
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('unauthorized', handleUnauthorized);
    };
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      // Call the real API
      const response = await apiService.login(email, password);

      if (response.success && response.data) {
        const { id, name, email: userEmail, role, token, refreshToken, createdAt, profileImageUrl } = response.data;

        // Create user object
        const userData = {
          id,
          name,
          email: userEmail,
          role,
          createdAt,
          profileImageUrl: formatProfileImageUrl(profileImageUrl),
        };

        // Store user data and tokens
        localStorage.setItem('vedic_ai_user', JSON.stringify(userData));
        localStorage.setItem('vedic_ai_token', token);
        localStorage.setItem('vedic_ai_refresh_token', refreshToken);

        setUser(userData);
        return { success: true, user: userData };
      }

      return { success: false, error: 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || 'An error occurred during login' 
      };
    }
  };

  // Signup function
  const signup = async (name, email, password, role) => {
    try {
      // Call the real API
      const response = await apiService.signup(name, email, password, role);

      if (response.success && response.data) {
        const { id, name: userName, email: userEmail, role: userRole, token, refreshToken, createdAt, profileImageUrl } = response.data;

        // Create user object
        const userData = {
          id,
          name: userName,
          email: userEmail,
          role: userRole,
          createdAt,
          profileImageUrl: formatProfileImageUrl(profileImageUrl),
        };

        // Store user data and tokens
        localStorage.setItem('vedic_ai_user', JSON.stringify(userData));
        localStorage.setItem('vedic_ai_token', token);
        localStorage.setItem('vedic_ai_refresh_token', refreshToken);

        setUser(userData);
        return { success: true, user: userData };
      }

      return { success: false, error: 'Signup failed' };
    } catch (error) {
      console.error('Signup error:', error);
      return { 
        success: false, 
        error: error.message || 'An error occurred during signup' 
      };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call the API to revoke refresh token
      await apiService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local storage
      localStorage.removeItem('vedic_ai_user');
      localStorage.removeItem('vedic_ai_token');
      localStorage.removeItem('vedic_ai_refresh_token');
      setUser(null);
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return user !== null;
  };

  // Update user state and local storage dynamically
  const updateUser = (updatedFields) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      if (updatedFields.profileImageUrl !== undefined) {
        updatedFields.profileImageUrl = formatProfileImageUrl(updatedFields.profileImageUrl);
      }
      const newUser = { ...prevUser, ...updatedFields };
      localStorage.setItem('vedic_ai_user', JSON.stringify(newUser));
      return newUser;
    });
  };

  const value = {
    user,
    login,
    signup,
    logout,
    isAuthenticated,
    loading,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;

